<template>
  <div class="asc-airline-agreement-grid">
    <asc-ag-grid-filter
      :key="`asc-airline-agreement-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getFormattedDateTime } from '@/utilities/dates'
import moment from 'moment'
import {
  stringTextMatcher,
  arrayTextMatcher,
  dateStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscAirlineAgreementGrid',

  components: {
    AscAgGridFilter,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    addAgreement: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridReady: this.handleGridReady,
        onGridSizeChanged: this.handleGridSizeChanged,
      }),
    }
  },

  computed: {
    mappedValues() {
      return this.values
    },
    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)
      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: 'ID',
          field: 'id',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Airline',
          field: 'airlineName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: arrayTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('airlineName'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Platform',
          field: 'masterModelName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('masterModelName'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Agreement Type',
          field: 'agreementTypeName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('agreementTypeName'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Start Date',
          field: 'fromDate',
          width: 155,
          minWidth: 155,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDateFloatFilter',
          floatingFilterComponentParams: {
            suppressFilterButton: true,
            items: this.values,
          },
          valueFormatter: data => getFormattedDateTime(data.value),
          comparator: this.sortDate,
        },
        {
          headerName: 'End Date',
          field: 'toDate',
          width: 155,
          minWidth: 155,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDateFloatFilter',
          floatingFilterComponentParams: {
            suppressFilterButton: true,
            items: this.values,
            dateFloatFilterConfig: {
              allowFutureDates: true,
            },
          },
          valueFormatter: data => getFormattedDateTime(data.value),
          comparator: this.sortDate,
        },
        {
          headerName: 'Expired',
          field: 'isExpired',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: [
              { text: 'Yes', value: 1 },
              { text: 'No', value: 2 },
            ],
            suppressFilterButton: true,
          },
          valueGetter: function (value) {
            const start = new Date(value.data.fromDate)
            const end = new Date(value.data.toDate)
            const today = new Date()

            return !moment(today).isBetween(start, end) ? 'Yes' : 'No'
          },
        },
        {
          headerName: 'Billable',
          field: 'isBillable',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: [
              { text: 'Yes', value: 1 },
              { text: 'No', value: 2 },
            ],
            suppressFilterButton: true,
          },
          valueGetter: value => (value.data.isBillable ? 'Yes' : 'No'),
        },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
.asc-sort-icon {
  color: $primary_black;
  position: absolute;
  top: 14px;
  right: 16px;
}

.asc-filter-icon {
  color: $primary_black;
}

.asc-airline-agreement-grid {
  &--button {
    padding: 10px 0;
  }
}
</style>
